import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Drawer, Form, Switch, Upload, Button, Modal, Space, message, Image } from 'antd';
// import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { getProjectPosterModulesListAllApi, updatePosterSettingsApi, selectionUploadFile } from '@services/Selection';
import { prepareBCOSSUrl } from '@utils/utils';
import PosterFields from '../PosterFields';
import posterExample from '@static/assets/img/posterExample.jpg';
import './index.less';

export default function PosterSettingDrawer({ open, onClose, onSaveSuccess }) {
    const params = useParams();
    const [form] = Form.useForm();
    const projectId = params.pid;

    const [posterEnabled, setPosterEnabled] = useState(false);
    const [bgUrl, setBgUrl] = useState(null); // 背景图 URL
    const [previewVisible, setPreviewVisible] = useState(false);
    const posterFieldsRef = useRef(null); // 使用 useRef 保存引用

    // 加载壁报设置
    const loadPosterSetting = useCallback(async () => {
        if (!projectId) return;

        try {
            const res = await getProjectPosterModulesListAllApi({ projectId });
            if (res && res.status_code === 200 && res.data) {
                const { project, list } = res.data;

                // 设置征集状态
                const enabled = project?.posterEnabled === true;
                setPosterEnabled(enabled);

                // 设置背景图
                let bgUrlValue = null;
                if (project?.posterSetting) {
                    try {
                        // posterSetting 是 JSON 字符串，需要解析
                        const posterSettingObj = JSON.parse(project.posterSetting);
                        bgUrlValue = posterSettingObj?.bgUrl || null;
                    } catch (e) {
                        console.error('解析 posterSetting 失败:', e);
                        bgUrlValue = null;
                    }
                }
                setBgUrl(bgUrlValue);

                form.setFieldsValue({
                    posterEnabled: enabled,
                });

                // 设置壁报字段列表
                if (posterFieldsRef?.current && Array.isArray(list)) {
                    posterFieldsRef.current.setFieldsList(list);
                }
            }
        } catch (error) {
            console.error('加载壁报设置失败:', error);
        }
    }, [projectId]);

    useEffect(() => {
        if (open) {
            loadPosterSetting();
        }
    }, [open, loadPosterSetting]);

    // 切换征集状态
    const handleSwitchChange = checked => {
        setPosterEnabled(checked);
        form.setFieldValue('posterEnabled', checked);
    };

    // 校验图片上传文件
    const checkImgUploadFile = (file, sizeMb) => {
        const typeList = ['image/jpg', 'image/jpeg', 'image/png'];
        const isImage = typeList.includes(file.type) || false;

        if (!isImage) {
            message.error('仅支持 JPG/PNG 图片格式');
            return false;
        }

        const metSize = file.size / 1024 / 1024 <= sizeMb;
        if (!metSize) {
            message.error(`图片文件大小限${sizeMb}MB`);
            return false;
        }

        return isImage && metSize;
    };

    // 上传背景图
    const uploadPosterImage = fileInfo => {
        const formData = new FormData();
        formData.append('file', fileInfo.file);
        formData.append('type', 13);

        selectionUploadFile(formData).then(res => {
            if (!res) {
                message.error('文件上传失败');
                return false;
            }

            if (res.status_code === 200) {
                // 上传成功后拼接域名，确保反显正常
                const fullUrl = prepareBCOSSUrl(res.data);
                setBgUrl(fullUrl);
                form.setFieldValue('posterSetting', { bgUrl: fullUrl });
                message.success('上传成功');
            } else {
                message.error('上传失败');
            }
        });
    };

    // 保存设置
    const handleSave = async () => {
        try {
            // 先校验表单
            await form.validateFields();

            // 校验壁报字段是否为空
            const fieldsList = posterFieldsRef?.current?.fieldsList || [];
            if (fieldsList.length === 0) {
                message.error('请至少添加一个壁报字段');
                return;
            }

            // 处理背景图 URL：如果有值，拼接上当前环境域名
            let fullBgUrl = bgUrl;
            if (bgUrl && !bgUrl.startsWith('http')) {
                fullBgUrl = prepareBCOSSUrl(bgUrl);
            }

            // 构建请求参数
            const params = {
                projectId,
                posterEnabled,
                posterSetting: fullBgUrl ? JSON.stringify({ bgUrl: fullBgUrl }) : undefined,
                moduleIds: fieldsList.map(item => item.id),
            };

            const res = await updatePosterSettingsApi(params);
            if (res && res.status_code === 200) {
                message.success('壁报设置成功');
                onClose();
                onSaveSuccess?.(); // 通知父组件刷新状态
            }
        } catch (error) {
            console.error('保存失败:', error);
        }
    };

    return (
        <>
            <Drawer
                title="壁报征集设置"
                placement="right"
                className="poster-setting-drawer"
                open={open}
                destroyOnHidden
                closeIcon={null}
                size={700}
                extra={<i className="bi bi-x-lg drawer-close-icon" onClick={onClose} />}
                footer={
                    <div className="poster-setting-footer">
                        <Space>
                            <Button onClick={onClose}>取消</Button>
                            <Button type="primary" onClick={handleSave}>
                                确认
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Form form={form} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                    <Form.Item label="征集状态" name="posterEnabled" valuePropName="checked" help={<span className="enabled-extra">开启后，本项目所有主题将新增“壁报”征集字段</span>}>
                        <Switch checkedChildren="开启" unCheckedChildren="关闭" onChange={handleSwitchChange} />
                    </Form.Item>

                    <Form.Item
                        label="壁报背景图"
                        extra={
                            <div className="upload-extra">
                                <div>宽 4480px * 高 1120px，文件大小不超过 10MB</div>
                                <div>
                                    请参考
                                    <a onClick={() => setPreviewVisible(true)} style={{ color: '#1890ff' }}>
                                        {' '}
                                        设计示例图
                                    </a>{' '}
                                    设计
                                </div>
                            </div>
                        }
                    >
                        {bgUrl && (
                            <div
                                className="uploaded-image"
                                style={{
                                    marginBottom: 8,
                                    width: 200,
                                    height: 200,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    backgroundColor: '#f5f5f5',
                                }}
                            >
                                <Image src={bgUrl} height={200} alt="海报背景预览" />
                            </div>
                        )}

                        <Upload beforeUpload={file => checkImgUploadFile(file, 10)} fileList={[]} customRequest={fileInfo => uploadPosterImage(fileInfo)} maxCount={1} accept="image/*">
                            <Button
                                icon={
                                    <span className="anticon">
                                        <i className="bi bi-upload" />
                                    </span>
                                }
                            >
                                {bgUrl ? '更改图片' : '上传图片'}
                            </Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item label="壁报字段" required>
                        <div className="poster-fields-header">
                            <Button type="primary" onClick={() => posterFieldsRef?.current?.handleAdd()}>
                                新增字段
                            </Button>
                        </div>
                    </Form.Item>

                    <PosterFields ref={posterFieldsRef} projectId={projectId} />
                </Form>
            </Drawer>

            {/* 示例图预览 */}
            <Modal title="设计示例图" open={previewVisible} onCancel={() => setPreviewVisible(false)} footer={null} width={800}>
                <div className="preview-image-container">
                    <img src={posterExample} alt="壁报设计示例图" style={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }} />
                </div>
            </Modal>
        </>
    );
}
